/* oxlint-disable react/prop-types */
// React
import { useState, useCallback, useEffect, useMemo } from "react"

// UI
import { cn } from "@/lib/utils"
import { Disclaimer } from "@/components/Disclaimer"
import { Heading } from "@/components/Typography"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts"

// I18n
import { useLang } from "@/context/useLang"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

// Types
import { ProjectTypeEnum } from "@/api/graphql/types"

// Data
import {
	METABASE_CROWDFUNDING_SUM_URL,
	endDateStatistics,
} from "@/constants/metabase"
import { useMetabasePublicQuestion } from "@/lib/metabase"

// I18n
import { useTrans } from "@/i18n"

interface FundedAmountPieChartProps {
	setSelectedProjectType: (projectType: ProjectTypeEnum) => void
}

interface ChartDataItem {
	name: string
	value: number
	projectType: ProjectTypeEnum
	color: string
}

interface CrowdfundingSumRow {
	fund_name: string
	sum: number
}

// Metabase returns one row per fund; we bucket rows onto the three pie slices
// by exact fund_name. A rename in Metabase drops the row (see console.warn in
// the transform), which is the desired fail-loud behavior.
const FUND_NAME_CROWDFUNDING = "Crowdfunding"
const FUND_NAME_ZONFONDS = "ZPD Zonfonds 1 B.V."
const ZONLEASE_FUND_NAMES = new Set(["Zonlease 1 B.V.", "Zonlease 2 B.V."])

/**
 * FundedAmountPieChart
 * Shows a pie chart of funded amount by category, sourced from the Metabase
 * "funded amount per fund" public question.
 */
export function FundedAmountPieChart({
	setSelectedProjectType,
}: FundedAmountPieChartProps) {
	// Dates
	const DateTime = useLocalizedDateTime()

	// Translate
	const t = useTrans("dashboard")
	const { formatCurrency } = useLang()

	// State
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
	const [chartSize, setChartSize] = useState({
		outerRadius: 80,
		innerRadius: 50,
	})
	// Responsive chart sizing via matchMedia instead of window.resize
	// to avoid conflicts with Playwright's viewport resize mechanism
	useEffect(() => {
		const smQuery = window.matchMedia("(max-width: 639px)")
		const lgQuery = window.matchMedia("(min-width: 1024px)")

		const update = () => {
			if (smQuery.matches) {
				setChartSize({ outerRadius: 60, innerRadius: 35 })
			} else if (lgQuery.matches) {
				setChartSize({ outerRadius: 120, innerRadius: 75 })
			} else {
				setChartSize({ outerRadius: 80, innerRadius: 50 })
			}
		}

		update()
		smQuery.addEventListener("change", update)
		lgQuery.addEventListener("change", update)
		return () => {
			smQuery.removeEventListener("change", update)
			lgQuery.removeEventListener("change", update)
		}
	}, [])

	// Query
	const { rows, isLoading } = useMetabasePublicQuestion<CrowdfundingSumRow>(
		METABASE_CROWDFUNDING_SUM_URL,
	)

	// Chart colors using the specified brand palette
	const chartColors = {
		crowdfunding: "#FFD900",
		solarFunds: "#0C65BB",
		solarLease: "#000000",
	}

	const data: ChartDataItem[] = useMemo(() => {
		let crowdfunding = 0
		let zonfonds = 0
		let zonlease = 0

		rows.forEach((row) => {
			const amount = Number(row.sum) || 0
			if (row.fund_name === FUND_NAME_CROWDFUNDING) {
				crowdfunding += amount
			} else if (row.fund_name === FUND_NAME_ZONFONDS) {
				zonfonds += amount
			} else if (ZONLEASE_FUND_NAMES.has(row.fund_name)) {
				zonlease += amount
			} else {
				console.warn(
					`FundedAmountPieChart: unknown fund_name "${row.fund_name}" from Metabase`,
				)
			}
		})

		const slices: ChartDataItem[] = []
		if (crowdfunding > 0) {
			slices.push({
				name: t("dashboard:funded_amount_pie_chart.bonds"),
				value: crowdfunding,
				projectType: ProjectTypeEnum.SolarShares,
				color: chartColors.crowdfunding,
			})
		}
		if (zonfonds > 0) {
			slices.push({
				name: t(`common:project.type.${ProjectTypeEnum.SolarFunds}`),
				value: zonfonds,
				projectType: ProjectTypeEnum.SolarFunds,
				color: chartColors.solarFunds,
			})
		}
		if (zonlease > 0) {
			slices.push({
				name: t(`common:project.type.${ProjectTypeEnum.SolarLease}`),
				value: zonlease,
				projectType: ProjectTypeEnum.SolarLease,
				color: chartColors.solarLease,
			})
		}
		return slices
	}, [
		rows,
		t,
		chartColors.crowdfunding,
		chartColors.solarFunds,
		chartColors.solarLease,
	])

	// Calculate total funded amount
	const totalFunded = data.reduce((sum, item) => sum + item.value, 0)

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload
			return (
				<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
					<p className="font-medium text-gray-900">{data.name}</p>
					<p className="text-sm text-gray-600">
						{formatCurrency(data.value, {
							maximumFractionDigits: 0,
						})}
					</p>
				</div>
			)
		}
		return null
	}

	const handleSliceClick = useCallback(
		(entry: ChartDataItem) => {
			setSelectedProjectType(entry.projectType)
		},
		[setSelectedProjectType],
	)

	// Handle loading state
	if (isLoading) {
		return (
			<div className="mb-8 w-full">
				<div className="mb-6 text-center">
					<div className="mb-4">
						<div className="mx-auto h-8 w-48 animate-pulse rounded bg-gray-200"></div>
						<div className="text-lg text-gray-600">
							{t(
								"dashboard:funded_amount_pie_chart.total_financed_via_zonhub",
							)}
						</div>
					</div>
				</div>
				<div className="w-full rounded-xl border border-gray-100 bg-white p-4 shadow-xs sm:p-6">
					<Heading
						as="h2"
						styleAs="h3"
						className="mb-2 text-gray-900"
					>
						{t(
							"dashboard:funded_amount_pie_chart.funded_amount_by_category",
						)}
					</Heading>
					<p className="mb-4 text-sm text-gray-600">
						{t("dashboard:statistics.platform.as_per_date", {
							date: DateTime.fromJSDate(
								endDateStatistics,
							).toLocaleString(),
						})}
					</p>
					<div className="flex flex-col gap-4 sm:gap-8 xl:flex-row">
						{/* Chart Loading */}
						<div className="w-full min-w-0 overflow-hidden xl:flex-1">
							<div className="flex h-64 items-center justify-center sm:h-80 md:h-96 xl:h-[400px]">
								<div className="size-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
							</div>
						</div>

						{/* Legend Loading */}
						<div className="w-full space-y-2 overflow-hidden xl:w-80 xl:shrink-0">
							{[1, 2, 3].map((index) => (
								<div
									key={`loading-legend-${index}`}
									className="flex items-center gap-3 rounded-lg bg-white p-3"
								>
									<div className="size-4 shrink-0 animate-pulse rounded-full bg-gray-200" />
									<div className="min-w-0 flex-1 overflow-hidden">
										<div className="mb-1 h-4 animate-pulse rounded bg-gray-200" />
										<div className="h-3 w-2/3 animate-pulse rounded bg-gray-200" />
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Handle no data
	if (!data || data.length === 0) {
		return (
			<div className="mb-8">
				<div className="mb-6 text-center">
					<div className="mb-4">
						<div className="text-3xl font-bold text-gray-900">
							{formatCurrency(0)}
						</div>
						<div className="text-lg text-gray-600">
							{t(
								"dashboard:funded_amount_pie_chart.total_financed_via_zonhub",
							)}
						</div>
					</div>
					<Heading
						as="h2"
						styleAs="h3"
						className="mb-2 text-gray-900"
					>
						{t(
							"dashboard:funded_amount_pie_chart.funded_amount_by_category",
						)}
					</Heading>
					<p className="mb-4 text-sm text-gray-600">
						{t("dashboard:statistics.platform.as_per_date", {
							date: DateTime.fromJSDate(
								endDateStatistics,
							).toLocaleString(),
						})}
					</p>
				</div>
				<div className="rounded-xl border border-gray-100 bg-white p-6 shadow-xs">
					<div className="py-12 text-center text-gray-500">
						{t("dashboard:funded_amount_pie_chart.no_funding_data")}
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="mb-8 w-full">
			<div className="mb-6 text-center">
				<div className="mb-4">
					<div className="text-3xl font-bold text-gray-900">
						{formatCurrency(totalFunded, {
							maximumFractionDigits: 0,
						})}
					</div>
					<div className="text-lg text-gray-600">
						{t(
							"dashboard:funded_amount_pie_chart.total_financed_via_zonhub",
						)}
					</div>
				</div>
			</div>

			<div className="w-full rounded-xl border border-gray-100 bg-white p-4 shadow-xs sm:p-6">
				<Heading as="h2" styleAs="h3" className="mb-2 text-gray-900">
					{t(
						"dashboard:funded_amount_pie_chart.funded_amount_by_category",
					)}
				</Heading>
				<p className="mb-4 text-sm text-gray-600">
					{t("dashboard:statistics.platform.as_per_date", {
						date: DateTime.fromJSDate(
							endDateStatistics,
						).toLocaleString(),
					})}
				</p>
				<div className="flex flex-col gap-4 sm:gap-8 xl:flex-row">
					{/* Chart */}
					<div className="w-full min-w-0 overflow-hidden xl:flex-1">
						<ResponsiveContainer
							width="100%"
							height={300}
							className="sm:h-80 md:h-96 xl:h-[400px]"
						>
							<PieChart>
								<Pie
									data={data}
									cx="50%"
									cy="50%"
									labelLine={false}
									outerRadius={chartSize.outerRadius}
									innerRadius={chartSize.innerRadius}
									startAngle={90}
									endAngle={450}
									fill="#8884d8"
									dataKey="value"
									onMouseEnter={(_, index) =>
										setHoveredIndex(index)
									}
									onMouseLeave={() => setHoveredIndex(null)}
									animationBegin={0}
									animationDuration={800}
								>
									{data.map((entry, index) => {
										const isHovered = hoveredIndex === index

										return (
											<Cell
												key={`cell-${index}`}
												fill={entry.color}
												stroke={
													isHovered
														? "#ffffff"
														: "#e5e7eb"
												}
												strokeWidth={isHovered ? 2 : 1}
												onClick={() =>
													handleSliceClick(entry)
												}
												className={cn(
													"cursor-pointer transition-all duration-300 focus:outline-hidden",
													isHovered &&
														"drop-shadow-lg",
												)}
												style={{
													filter: isHovered
														? "brightness(1.1)"
														: "brightness(1)",
													strokeLinejoin: "round",
													outline: "none",
												}}
											/>
										)
									})}
								</Pie>
								<Tooltip content={<CustomTooltip />} />
							</PieChart>
						</ResponsiveContainer>
					</div>

					{/* Legend */}
					<div className="w-full space-y-2 overflow-hidden xl:w-80 xl:shrink-0">
						{data.map((entry, index) => {
							const percentage = (
								(entry.value / totalFunded) *
								100
							).toFixed(1)

							return (
								<button
									type="button"
									key={`legend-${entry.name}-${index}`}
									onClick={() => handleSliceClick(entry)}
									className="focus:ring-primary-500 flex w-full cursor-pointer items-center gap-3 rounded-lg bg-white p-3 text-left transition-all duration-200 hover:bg-gray-50 focus:ring-2 focus:outline-hidden"
									aria-label={`Filter by ${entry.name}, ${formatCurrency(entry.value, { maximumFractionDigits: 0 })}, ${percentage} percent`}
								>
									<div
										className="size-4 shrink-0 rounded-full border border-gray-200"
										style={{
											backgroundColor: entry.color,
										}}
										aria-hidden="true"
									/>
									<div className="min-w-0 flex-1 overflow-hidden">
										<div className="truncate font-medium text-gray-900">
											{entry.name}
										</div>
										<div className="truncate text-sm text-gray-500">
											{formatCurrency(entry.value, {
												maximumFractionDigits: 0,
											})}{" "}
											• {percentage}%
										</div>
									</div>
								</button>
							)
						})}
					</div>
				</div>
			</div>

			{/** Disclaimer */}
			<Disclaimer
				className="mt-4"
				title={t("dashboard:stats.platform_stats.disclaimer.title")}
				message={t("dashboard:stats.platform_stats.disclaimer.text")}
			/>
		</div>
	)
}
